import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api";
import { AuthUser, Department } from "@/lib/types";
import { getErrorMessage } from "@/lib/utils";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";

// Re-export for consumers
export type { Department } from "@/lib/types";

export interface TicketType {
  id: string;
  name: string;
  created_at?: string;
}

export interface Stage {
  id: string;
  name: string;
  color: string;
  position: number;
  type?: 'default' | 'resolved' | 'closed';
  created_at?: string;
  assigneeIds?: string[];
  assignees?: AuthUser[];
}

// Departments Hook
export function useDepartments() {
  const queryClient = useQueryClient();

  const departmentsQuery = useQuery({
    queryKey: ["departments"],
    queryFn: async () => {
      const response = await apiClient.getDepartments();
      return response;
    },
  });

  const createDepartmentMutation = useMutation({
    mutationFn: async (data: { name: string; teamLeaderId?: string; teamLeaderIds?: string[]; agentsIds?: string[] }) => {
      const response = await apiClient.createDepartment(data);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["departments"] });
      queryClient.invalidateQueries({ queryKey: ["config"] });
      toast.success("Department created successfully");
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, "Failed to create department"));
    },
  });

  const updateDepartmentMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: { name?: string; isActive?: boolean; teamLeaderId?: string | null; teamLeaderIds?: string[]; agentsIds?: string[] } }) => {
      const response = await apiClient.updateDepartment(id, data);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["departments"] });
      queryClient.invalidateQueries({ queryKey: ["config"] });
      toast.success("Department updated successfully");
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, "Failed to update department"));
    },
  });

  const deleteDepartmentMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiClient.deleteDepartment(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["departments"] });
      queryClient.invalidateQueries({ queryKey: ["config"] });
      toast.success("Department deleted successfully");
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, "Failed to delete department"));
    },
  });

  return {
    departments: (departmentsQuery.data ?? []) as Department[],
    isLoading: departmentsQuery.isLoading,
    error: departmentsQuery.error,
    createDepartment: createDepartmentMutation,
    updateDepartment: updateDepartmentMutation,
    deleteDepartment: deleteDepartmentMutation,
    refetch: () => queryClient.invalidateQueries({ queryKey: ["departments"] }),
  };
}

export function useDepartmentById(id: string | undefined) {
  return useQuery({
    queryKey: ["department", id],
    queryFn: () => apiClient.getDepartmentById(id!),
    enabled: !!id,
  });
}

export function useAvailableTeamLeaders() {
  return useQuery({
    queryKey: ["availableTeamLeaders"],
    queryFn: () => apiClient.getAvailableTeamLeaders(),
  });
}

export function useAvailableAgents() {
  return useQuery({
    queryKey: ["availableAgents"],
    queryFn: () => apiClient.getAvailableAgents(),
  });
}

/** Departments the current user leads (team_leader). Uses GET /config/departments and filters by teamLeaders. */
export function useLeadDepartment() {
  const { user, isTeamLeader } = useAuth();
  const { departments, isLoading } = useDepartments();
  const leadDepartments = isTeamLeader && user?.id && departments.length > 0
    ? departments.filter((d) => {
        const leaders = d.teamLeaders ?? (d.teamLeader ? [d.teamLeader] : []);
        return leaders.some((tl: { user?: { id: string }; id?: string }) => (tl as { user?: { id: string } }).user?.id === user.id || (tl as { id: string }).id === user.id);
      })
    : [];
  const leadDepartment = leadDepartments[0] ?? null;
  const leadDepartmentId = leadDepartment?.id ?? null;
  return { leadDepartment, leadDepartmentId, leadDepartments, isLoading };
}

/** Department the current user (agent) is assigned to. Uses GET /config/departments and finds department where agents include this user. */
export function useAgentDepartment() {
  const { user, isAgent } = useAuth();
  const { departments, isLoading } = useDepartments();
  const agentDepartment = isAgent && user?.id && departments.length > 0
    ? departments.find((d) => (d.agents ?? []).some((a: { id: string }) => a.id === user.id))
    : null;
  const agentDepartmentId = agentDepartment?.id ?? null;
  return { agentDepartment, agentDepartmentId, isLoading };
}

// Ticket Types Hook
export function useTicketTypes() {
  const queryClient = useQueryClient();

  const ticketTypesQuery = useQuery({
    queryKey: ["ticketTypes"],
    queryFn: async () => {
      const response = await apiClient.getTicketTypes();
      return response;
    },
  });

  const createTicketTypeMutation = useMutation({
    mutationFn: async (data: { name: string }) => {
      const response = await apiClient.createTicketType(data);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ticketTypes"] });
      queryClient.invalidateQueries({ queryKey: ["config"] });
      toast.success("Ticket type created successfully");
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, "Failed to create ticket type"));
    },
  });

  const updateTicketTypeMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: { name: string } }) => {
      const response = await apiClient.updateTicketType(id, data);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ticketTypes"] });
      queryClient.invalidateQueries({ queryKey: ["config"] });
      toast.success("Ticket type updated successfully");
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, "Failed to update ticket type"));
    },
  });

  const deleteTicketTypeMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiClient.deleteTicketType(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ticketTypes"] });
      queryClient.invalidateQueries({ queryKey: ["config"] });
      toast.success("Ticket type deleted successfully");
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, "Failed to delete ticket type"));
    },
  });

  return {
    ticketTypes: (ticketTypesQuery.data ?? []) as TicketType[],
    isLoading: ticketTypesQuery.isLoading,
    error: ticketTypesQuery.error,
    createTicketType: createTicketTypeMutation,
    updateTicketType: updateTicketTypeMutation,
    deleteTicketType: deleteTicketTypeMutation,
    refetch: () => queryClient.invalidateQueries({ queryKey: ["ticketTypes"] }),
  };
}

// Stages Hook
export function useStages() {
  const queryClient = useQueryClient();

  const stagesQuery = useQuery({
    queryKey: ["stages"],
    queryFn: async () => {
      const response = await apiClient.getStages();
      return response;
    },
  });

  const createStageMutation = useMutation({
    mutationFn: async (data: { name: string; color: string; position: number; type?: 'default' | 'resolved' | 'closed'; assigneeIds?: string[] }) => {
      const response = await apiClient.createStage(data);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["stages"] });
      queryClient.invalidateQueries({ queryKey: ["config"] });
      toast.success("Stage created successfully");
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, "Failed to create stage"));
    },
  });

  const updateStageMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: { name: string; color: string; type?: 'default' | 'resolved' | 'closed'; assigneeIds?: string[] } }) => {
      const response = await apiClient.updateStage(id, data);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["stages"] });
      queryClient.invalidateQueries({ queryKey: ["config"] });
      toast.success("Stage updated successfully");
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, "Failed to update stage"));
    },
  });

  const deleteStageMutation = useMutation({
    mutationFn: async (id: string) => {
      return apiClient.deleteStage(id);
    },
    onSuccess: (data) => {
      if (data?.success) {
        queryClient.invalidateQueries({ queryKey: ["stages"] });
        queryClient.invalidateQueries({ queryKey: ["config"] });
        queryClient.invalidateQueries({ queryKey: ["tickets"] });
        toast.success("Stage deleted successfully");
      }
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, "Failed to delete stage"));
    },
  });

  const reassignStageTicketsMutation = useMutation({
    mutationFn: async (body: { fromStageId: string; toStageId: string; ticketsIds: string[] }) => {
      await apiClient.reassignStageTickets(body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["stages"] });
      queryClient.invalidateQueries({ queryKey: ["config"] });
      queryClient.invalidateQueries({ queryKey: ["tickets"] });
      toast.success("Tickets reassigned and stage deleted successfully");
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, "Failed to reassign tickets"));
    },
  });

  const reorderStagesMutation = useMutation({
    mutationFn: async (stageIds: string[]) => {
      await apiClient.reorderStages(stageIds);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["stages"] });
      queryClient.invalidateQueries({ queryKey: ["config"] });
      toast.success("Stages reordered successfully");
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, "Failed to reorder stages"));
    },
  });

  return {
    stages: (stagesQuery.data ?? []) as Stage[],
    isLoading: stagesQuery.isLoading,
    error: stagesQuery.error,
    createStage: createStageMutation,
    updateStage: updateStageMutation,
    deleteStage: deleteStageMutation,
    reassignStageTickets: reassignStageTicketsMutation,
    reorderStages: reorderStagesMutation,
    refetch: () => queryClient.invalidateQueries({ queryKey: ["stages"] }),
  };
}
